import { useMemo } from "react";
import * as THREE from "three";
import { buildRackFrame, type RackFrameOptions, type RackPost } from "./rackFrame";
import type { RackVolume } from "./rackVolume";
import { StaticInstances } from "./StaticInstances";
import { chunkByGrid } from "./palletLoad";
import { xrayRef } from "./xray";

interface Props {
  volumes: RackVolume[];
  options?: RackFrameOptions;
  /** ADR 196 product search: fade the steel so matched loads read through it.
   *  Shadows off while ghosted — a transparent post casting a solid shadow
   *  reads as a rendering bug. */
  ghost?: boolean;
}

/** Just above the floor overlays, same as OccupancyLayer. */
const FLOOR_Y = 0.03;

// Brazilian-standard rack livery, lifted from warehouse_UL.glb — the sRGB form of the
// model's LINEAR M_Rack_* baseColorFactors (three converts the hex back to linear, so it
// matches the model): navy uprights (M_Rack_Ferro_Blue), orange load-beams (M_Rack_Suporte_Orange).
const NAVY = "#253667";
const ORANGE = "#ff6a00";
const POST_W = 0.09;   // upright cross-section, m
const BEAM_W = 0.05;   // beam depth, m
const BEAM_H = 0.14;   // beam height, m — a touch taller (live feedback)

interface BeamXform { len: number; ang: number; x: number; y: number; z: number }

const _pos = new THREE.Vector3();
const _quat = new THREE.Quaternion();
const _scl = new THREE.Vector3();
const _zAxis = new THREE.Vector3(0, 0, 1);

function composePost(p: RackPost, out: THREE.Matrix4) {
  _quat.identity();
  out.compose(
    _pos.set(p.x, p.y, Math.max(0.01, p.top) / 2),
    _quat,
    _scl.set(POST_W, POST_W, Math.max(0.02, p.top)),
  );
}

function composeBeam(b: BeamXform, out: THREE.Matrix4) {
  _quat.setFromAxisAngle(_zAxis, b.ang);
  out.compose(
    _pos.set(b.x, b.y, b.z),
    _quat,
    _scl.set(Math.max(0.02, b.len), BEAM_W, BEAM_H),
  );
}

/**
 * Kit-of-parts rack frame (ADR 185/187): the structure volumes rendered as a real rack —
 * navy upright frames between bays + orange load-beams spanning post-to-post at each
 * level top — instead of the translucent slot box. `StaticInstances` per part type and
 * ~24 m grid chunk (ADR 199 — the drei `<Instances>` version put every post and beam in
 * the scene graph, ~29k objects at JSL scale), so a 100 m aisle of thousands of members
 * stays cheap and the frustum culls whole blocks when the camera is inside an aisle.
 * The steel does not cast shadows: the scene's shadow camera never covered more than a
 * corner patch, so the pass was vertex cost with nothing visible to show for it.
 *
 * Parametric: built to the warehouse's REAL dimensions from `buildRackFrame`, in the same
 * `rotation-x = -π/2` floor group as OccupancyLayer (local z is world height). The colours
 * are the only thing reused from warehouse_UL.glb — its racks are fixed-size welded runs.
 */
export function RackFrameLayer({ volumes, options, ghost = false }: Props) {
  const { posts, beams } = useMemo(() => buildRackFrame(volumes, options), [volumes, options]);
  const beamXforms = useMemo(() => beams.map((b): BeamXform => {
    const dx = b.bx - b.ax, dy = b.by - b.ay;
    return { len: Math.hypot(dx, dy), ang: Math.atan2(dy, dx), x: (b.ax + b.bx) / 2, y: (b.ay + b.by) / 2, z: b.z };
  }), [beams]);
  const postChunks = useMemo(() => chunkByGrid(posts), [posts]);
  const beamChunks = useMemo(() => chunkByGrid(beamXforms), [beamXforms]);

  if (posts.length === 0) return null;

  const g = ghost ? "g" : "";
  return (
    <group rotation={[-Math.PI / 2, 0, 0]} position={[0, FLOOR_Y, 0]}>
      {[...postChunks].map(([key, items]) => (
        <StaticInstances key={`p${g}:${key}:${items.length}`} items={items} compose={composePost} cullable>
          <boxGeometry args={[1, 1, 1]} />
          <meshStandardMaterial ref={xrayRef} color={NAVY} metalness={0.4} roughness={0.35}
                                transparent={ghost} opacity={ghost ? 0.15 : 1} depthWrite={!ghost} />
        </StaticInstances>
      ))}
      {[...beamChunks].map(([key, items]) => (
        <StaticInstances key={`b${g}:${key}:${items.length}`} items={items} compose={composeBeam} cullable>
          <boxGeometry args={[1, 1, 1]} />
          <meshStandardMaterial ref={xrayRef} color={ORANGE} metalness={0.4} roughness={0.35}
                                transparent={ghost} opacity={ghost ? 0.15 : 1} depthWrite={!ghost} />
        </StaticInstances>
      ))}
    </group>
  );
}
